import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import CryptoJS from "crypto-js";
import { Box, Flex, HStack, Spacer, useMediaQuery, Text } from "native-base";
import Dropdown from "../../../../components/dropdown";
import InputField from "../../../../components/input";
import { MOBILE_VIEW_WIDTH } from "../../../../utils/screens";
import FormHeader from "../../../../components/formHeader";
import MultiSelectDropdown from "../../../../components/multiselect";
import CommonBadge from "../../../../components/badge";
import config from "../../../../utils/config";
import { getQuizzesAPI } from '../../../../api/apis/quicklinks'
import FileUploader from "../../../../containers/fileUpload";
import ViewUploadImageCards from "../../../../containers/viewUploadImageCards";
import { COMMONFONTWEIGHT } from "../../../../utils/fonts";
import { validateAndResizeImage } from "../../../../utils/imageValidation";

const EventBasicDetails = forwardRef(
  (
    {
      eventFormData,
      setEventFormData,
      eventErrorFormData,
      setDefaultErrorFrom,
      eventAgency,
      forceRemount,
      childref,
      showModal,
      edit = false,
      showToaster,
    },
    ref
  ) => {
    const [eventUpdateFormData, setEventUpdateFormData] =
      useState(eventFormData);
    const [quizOptions, setQuizOptions] = useState([]);
    const [selectedLogoFiles, setSelectedLogoFiles] = useState([]);
    const [MobileWindow] = useMediaQuery({
      maxWidth: MOBILE_VIEW_WIDTH,
    });
    const bytes = CryptoJS.AES.decrypt(
      sessionStorage.getItem("content"),
      config.userKey
    );
    const jsonString = bytes.toString(CryptoJS.enc.Utf8);
    const userInfo = JSON.parse(jsonString);

    const isPremiumOptions = [
      { id: "1", value: 'yes', label: "Yes" },
      { id: "2", value: 'no', label: "No" },
    ]

    // Add "Self" option to the agency list
    const agencyOptions = [
      { 
        id: "self", 
        value: "self", 
        label: "Self",
        isSelfOption: true 
      },
      ...(eventAgency || [])
    ];

    // Fetch quiz options on component mount
    useEffect(() => {
      const fetchQuizOptions = async () => {
        try {
          const response = await getQuizzesAPI();
          if (response && response.data) {
            const mappedQuizOptions = response.data.data.data.map((quiz) => ({
              id: quiz._id,
              value: quiz._id,
              label: quiz.title
            }));
            setQuizOptions(mappedQuizOptions);
          }
        } catch (error) {
          console.error("Error fetching quiz options:", error);
          setQuizOptions([]);
        }
      };

      fetchQuizOptions();
    }, []);

    useEffect(() => {
      if (showModal) {
        let data = { ...eventUpdateFormData };
        let dataKeys = [
          "agency",
          "business",
          "coordinator_name",
          "email",
          "phone",
          "coordinator_name_2",
          "email_2",
          "phone_2",
          "coordinator_name_3",
          "email_3",
          "phone_3",
          "is_premium",
          "quiz_id",
          "logo"
        ];
        Object.keys(eventUpdateFormData).map((key) => {
          if (!dataKeys.includes(key)) delete data[key];
        });
        setEventUpdateFormData(data);
      } else {
        // Preserve existing selections when coming back
        const updatedData = { ...eventFormData };
        if (eventUpdateFormData?.agency && eventUpdateFormData?.agency?.length > 0) {
          updatedData.agency = eventUpdateFormData?.agency;
          updatedData.self_agency_approval = eventUpdateFormData?.self_agency_approval;
        }
        if (eventUpdateFormData?.is_premium !== undefined) {
          updatedData.is_premium = eventUpdateFormData?.is_premium;
        }
        if (eventUpdateFormData?.quiz_id !== undefined) {
          updatedData.quiz_id = eventUpdateFormData?.quiz_id;
        }
        if (eventUpdateFormData?.logo !== undefined) {
          updatedData.logo = eventUpdateFormData?.logo;
        }
        setEventUpdateFormData(updatedData);
      }
    }, [showModal, eventFormData]);

    // Handle logo file display when component mounts or data changes
    useEffect(() => {
      if (eventUpdateFormData["logo"] && eventUpdateFormData["logo"].length !== 0) {
        if (!edit) {
          // For new uploads, create object URLs from File objects
          const imageUrls = Array.from(eventUpdateFormData["logo"]).map(
            (item) => URL.createObjectURL(item)
          );
          setSelectedLogoFiles(imageUrls);
        } else {
          // For edit mode, handle server data structure
          if (Array.isArray(eventUpdateFormData["logo"])) {
            const imageUrls = eventUpdateFormData["logo"].map((item) => {
              if (item instanceof File) {
                return URL.createObjectURL(item);
              } else if (typeof item === 'string') {
                return item; // Already a URL
              } else if (item && item.file_name) {
                // Server data structure: { file_name: "path", ... }
                return item.file_name;
              }
              return item;
            });
            setSelectedLogoFiles(imageUrls);
          }
        }
      } else {
        setSelectedLogoFiles([]);
      }
    }, [eventUpdateFormData.logo, edit]);
    

    const onInputChange = (e, eName) => {
      setDefaultErrorFrom();
      let detail = { ...eventUpdateFormData };
      let validAgencies = [];

      if (['agency', 'is_premium', 'quiz_id'].includes(eName)) {
        if (eName === 'agency') {
          // Check if "Self" was selected
          const hasSelfOption = e?.some((item) => item.isSelfOption);
          let isBlocked = false;

          if (hasSelfOption) {
            // If "Self" is selected, only keep "Self" and disable other options
            detail[eName] = e.filter((item) => item.isSelfOption);
            detail['self_agency_approval'] = true;
          } else {
            // Normal agency selection
            e?.map((list) => {
              if (list.blocked) {
                isBlocked = true;
              } else {
                validAgencies.push(list);
              }
            });

            if (isBlocked) {
              showToaster();
            }

            detail[eName] = validAgencies;
            detail['self_agency_approval'] = false;
          }
        } else if (eName === 'is_premium') {
          detail[eName] = e === 'yes';
        }
        else if (eName === 'quiz_id') {
          detail[eName] = e;
        }
        setEventUpdateFormData(detail);
        return;
      }

      let value = e.target.value;
      if ((eName === "phone" || eName === "phone_2" || eName === "phone_3") && (isNaN(value) || value.length > 10)) {
        return;
      }
      detail[eName] = value;
      setEventUpdateFormData(detail);
    };

    const handleSelectedValues = (ind) => {
      let detail = { ...eventUpdateFormData };
      detail["agency"] = eventUpdateFormData.agency?.filter(
        (item) => item.id !== ind
      );
      
      // If removing "Self", remove the self_agency_approval flag
      if (ind === "self") {
        delete detail["self_agency_approval"];
      }
      
      setEventUpdateFormData(detail);
      return;
    };

    const handleLogoFilesSelect = async (file) => {
      try {
        // Validate the uploaded file
        const validatedFile = await validateAndResizeImage(file[0]);
        
        const imageUrls = [URL.createObjectURL(validatedFile)];
        setSelectedLogoFiles(imageUrls);
        
        let detail = { ...eventUpdateFormData };
        detail["logo"] = [validatedFile];
        setEventUpdateFormData(detail);
      } catch (error) {
        // Show toaster with error message
        showToaster(error.error || 'Invalid image. Please upload an image between 200x200 and 1024x1024 pixels. Non-square images will be automatically resized to 800x800.');
        // Clear any previously selected files
        setSelectedLogoFiles([]);
        let detail = { ...eventUpdateFormData };
        detail["logo"] = [];
        setEventUpdateFormData(detail);
      }
    };

    const onDeleteLogoImage = (ind) => {
      setSelectedLogoFiles([]);
      let detail = { ...eventUpdateFormData };
      detail["logo"] = [];
      setEventUpdateFormData(detail);
    };

    const onSubmitDataInput = () => {
      // Ensure is_premium is always a boolean in the final payload
      const finalData = { ...eventUpdateFormData };
      if (typeof finalData.is_premium === 'string' && finalData.is_premium !== '') {
        finalData.is_premium = finalData.is_premium === 'yes';
      }

      // Ensure self_agency_approval is properly set based on agency selection
      if (finalData?.agency && finalData?.agency?.length > 0) {
        const hasSelfOption = finalData?.agency?.some((item) => item?.isSelfOption);
        if (hasSelfOption) {
          finalData.self_agency_approval = true;
        } else {
          finalData.self_agency_approval = false;
        }
      }

      return finalData;
    };

    useImperativeHandle(ref, () => ({
      onSubmitDataInput,
    }));

    // Helper function to get the display value for is_premium dropdown
    const getIsPremiumDisplayValue = () => {
      if (typeof eventUpdateFormData.is_premium === 'boolean') {
        return eventUpdateFormData.is_premium ? 'yes' : 'no';
      }
      return eventUpdateFormData.is_premium;
    };

    return (
      <>
        {!edit && <FormHeader text="Basic Details" />}
        <Spacer axis="X" size={4}></Spacer>
        <HStack
          space={MobileWindow ? 4 : 5}
          direction={MobileWindow ? "column" : "row"}
          mb={3}
        >
          <Box flex={1}>
            <Dropdown
              name="is_premium"
              dropItems={isPremiumOptions}
              selectedValues={getIsPremiumDisplayValue()
              }
              setSelectedValues={onInputChange}
              label="Premium Booking"
              placeholder="Premium Booking or not"
              isInvalid={eventErrorFormData.is_premium}
              errorMessage={eventErrorFormData.is_premium}
              // important
              forceRemount={forceRemount}
            />
          </Box>
          <Box flex={1}>
            <Dropdown
              name="quiz_id"
              dropItems={quizOptions}
              selectedValues={
                eventUpdateFormData?.quiz_id
              }
              setSelectedValues={onInputChange}
              label="Quiz Id"
              placeholder="Quiz Id"
              isInvalid={eventErrorFormData.quiz_id}
              errorMessage={eventErrorFormData.quiz_id}
              // important
              forceRemount={forceRemount}
            />
          </Box>
        </HStack>
        <HStack
          space={MobileWindow ? 1 : 5}
          direction={MobileWindow ? "column" : "row"}
          display={MobileWindow && "block"}
        >
          <Box flex={1}>
            <MultiSelectDropdown
              text="Agency"
              name="agency"
              label="Agency"
              selectedValues={eventUpdateFormData.agency}
              setSelectedValues={onInputChange}
              isInvalid={eventErrorFormData.agency}
              errorMessage={eventErrorFormData.agency}
              dropItems={agencyOptions}
              important
              forceRemount={forceRemount}
              // Disable other options when "Self" is selected
              isOptionDisabled={(option) => 
                eventUpdateFormData.self_agency_approval && !option.isSelfOption
              }
            />
            {MobileWindow && (
              <Flex direction="row" wrap="wrap" mt={3} space={2}>
                {eventUpdateFormData.agency.map((item, ind) => (
                  <Box key={ind} mb={1} mr={2}>
                    <CommonBadge
                      text={item.label}
                      handleSelectedValues={handleSelectedValues}
                      selectedIndex={item.id}
                    />
                  </Box>
                ))}
              </Flex>
            )}
          </Box>
          <Box flex={1}>
            <InputField
              name="business"
              value={
                // eventFormData.business === ""
                //   ? eventUpdateFormData.business
                //   : eventFormData.business
                userInfo?.company
              }
              label="Business Name"
              placeholder="Enter Business Name"
              onChange={onInputChange}
              isInvalid={eventErrorFormData.business}
              errorMessage={eventErrorFormData.business}
              important
              forceRemount={forceRemount}
              ref={childref}
              isDisabled={userInfo?.company && true}
            />
          </Box>
        </HStack>
        {!MobileWindow && (
          <Flex direction="row" wrap="wrap" mt={3} space={2}>
            {eventUpdateFormData.agency.map((item, ind) => (
              <Box key={ind} mb={1} mr={2}>
                <CommonBadge
                  text={item.label}
                  handleSelectedValues={handleSelectedValues}
                  selectedIndex={item.id}
                />
              </Box>
            ))}
          </Flex>
        )}
        <Spacer size={2}></Spacer>
        <HStack space={5} direction={MobileWindow ? "column" : "row"}>
          <Box flex={1} mt={MobileWindow && 2}>
            <InputField
              name="coordinator_name"
              value={
                // eventFormData.coordinator_name === ""
                //   ? eventUpdateFormData.coordinator_name
                //   : eventFormData.coordinator_name
                userInfo?.display_name
              }
              label="Coordinator Name 1"
              placeholder="Enter Coordinator Name 1"
              onChange={onInputChange}
              isInvalid={eventErrorFormData.coordinator_name}
              errorMessage={eventErrorFormData.coordinator_name}
              important
              forceRemount={forceRemount}
              ref={childref}
              isDisabled={userInfo?.display_name && true}
            />
          </Box>
          <Box flex={1}>
            <InputField
              name="email"
              value={
                // eventFormData.email === ""
                //   ? eventUpdateFormData.email
                //   : eventFormData.email
                userInfo?.email
              }
              label="Email ID 1"
              placeholder="Enter Email ID 1"
              onChange={onInputChange}
              isInvalid={eventErrorFormData.email}
              errorMessage={eventErrorFormData.email}
              important
              forceRemount={forceRemount}
              ref={childref}
              isDisabled={userInfo?.email && true}
            />
          </Box>
        </HStack>
        <Spacer size={5}></Spacer>
        <HStack space={5} direction={MobileWindow ? "column" : "row"}>
          <Box flex={1} pb={MobileWindow && "10px"}>
            <InputField
              name="phone"
              value={
                eventFormData.phone === ""
                  ? eventUpdateFormData.phone
                  : eventFormData.phone
              }
              label="Contact Number 1"
              placeholder="Enter Contact Number 1"
              onChange={onInputChange}
              isInvalid={eventErrorFormData.phone}
              errorMessage={eventErrorFormData.phone}
              forceRemount={forceRemount}
              ref={childref}
            />
          </Box>
          {!MobileWindow && <Box flex={1}></Box>}
        </HStack>
        <Spacer size={5}></Spacer>
        <HStack space={5} direction={MobileWindow ? "column" : "row"}>
          <Box flex={1} mt={MobileWindow && 2}>
            <InputField
              name="coordinator_name_2"
              value={
                eventFormData.coordinator_name_2 === ""
                  ? eventUpdateFormData.coordinator_name_2
                  : eventFormData.coordinator_name_2
              }
              label="Coordinator Name 2"
              placeholder="Enter Coordinator Name 2"
              onChange={onInputChange}
              isInvalid={eventErrorFormData.coordinator_name_2}
              errorMessage={eventErrorFormData.coordinator_name_2}
              forceRemount={forceRemount}
              ref={childref}
            />
          </Box>
          <Box flex={1}>
            <InputField
              name="email_2"
              value={
                eventFormData.email_2 === ""
                  ? eventUpdateFormData.email_2
                  : eventFormData.email_2
              }
              label="Email ID 2"
              placeholder="Enter Email ID 2"
              onChange={onInputChange}
              isInvalid={eventErrorFormData.email_2}
              errorMessage={eventErrorFormData.email_2}
              forceRemount={forceRemount}
              ref={childref}
            />
          </Box>
        </HStack>
        <Spacer size={5}></Spacer>
        <HStack space={5} direction={MobileWindow ? "column" : "row"}>
          <Box flex={1} pb={MobileWindow && "10px"}>
            <InputField
              name="phone_2"
              value={
                eventFormData.phone_2 === ""
                  ? eventUpdateFormData.phone_2
                  : eventFormData.phone_2
              }
              label="Contact Number 2"
              placeholder="Enter Contact Number 2"
              onChange={onInputChange}
              isInvalid={eventErrorFormData.phone_2}
              errorMessage={eventErrorFormData.phone_2}
              forceRemount={forceRemount}
              ref={childref}
            />
          </Box>
          {!MobileWindow && <Box flex={1}></Box>}
        </HStack>
        <Spacer size={5}></Spacer>
        <HStack space={5} direction={MobileWindow ? "column" : "row"}>
          <Box flex={1} mt={MobileWindow && 2}>
            <InputField
              name="coordinator_name_3"
              value={
                eventFormData.coordinator_name_3 === ""
                  ? eventUpdateFormData.coordinator_name_3
                  : eventFormData.coordinator_name_3
              }
              label="Coordinator Name 3"
              placeholder="Enter Coordinator Name 3"
              onChange={onInputChange}
              isInvalid={eventErrorFormData.coordinator_name_3}
              errorMessage={eventErrorFormData.coordinator_name_3}

              forceRemount={forceRemount}
              ref={childref}
            />
          </Box>
          <Box flex={1}>
            <InputField
              name="email_3"
              value={
                eventFormData.email_3 === ""
                  ? eventUpdateFormData.email_3
                  : eventFormData.email_3
              }
              label="Email ID 3"
              placeholder="Enter Email ID 3"
              onChange={onInputChange}
              isInvalid={eventErrorFormData.email_3}
              errorMessage={eventErrorFormData.email_3}
              forceRemount={forceRemount}
              ref={childref}
            />
          </Box>
        </HStack>
        <Spacer size={5}></Spacer>
        <HStack space={5} direction={MobileWindow ? "column" : "row"}>
          <Box flex={1} pb={MobileWindow && "10px"}>
            <InputField
              name="phone_3"
              value={
                eventFormData.phone_3 === ""
                  ? eventUpdateFormData.phone_3
                  : eventFormData.phone_3
              }
              label="Contact Number 3"
              placeholder="Enter Contact Number 3"
              onChange={onInputChange}
              isInvalid={eventErrorFormData.phone_3}
              errorMessage={eventErrorFormData.phone_3}
              forceRemount={forceRemount}
              ref={childref}
            />
          </Box>
          {!MobileWindow && <Box flex={1}></Box>}
        </HStack>
        
        <Spacer axis="X" size={4}></Spacer>
        <HStack space={5} direction={MobileWindow ? "column" : "row"}>
          <Box flex={!MobileWindow && 1}>
            <Text
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              color={"rgb(115, 115, 115)"}
            >
              Upload Logo
            </Text>
            <FileUploader
              files={selectedLogoFiles}
              show={false}
              setFiles={handleLogoFilesSelect}
              accept={["png", "jpg", "jpeg", "gif"]}
              types={".png, .jpg, .jpeg, .gif"}
              forceRemount={forceRemount}
            />
            <HStack flex={1} flexWrap={"wrap"}>
              {selectedLogoFiles.length > 0 && selectedLogoFiles[0] !== "" && (
                <ViewUploadImageCards
                  ind={0}
                  url={selectedLogoFiles[0]}
                  onDeleteImage={onDeleteLogoImage}
                />
              )}
            </HStack>
          </Box>
          <Box flex={1}></Box>
        </HStack>
      </>
    );
  }
);

export default EventBasicDetails;
